import { computed } from 'vue'
import { useAuthStore } from '@/stores/auth'

/**
 * Composable que unifica a lógica de permissão de menus.
 * Consumido tanto pelo MainDashboard (sidebar) quanto pelo ModalMapaMenus.
 */
export function useMenuPermissao (menuConfig) {
  const authStore = useAuthStore()

  /**
   * Verifica se o usuário tem permissão para uma chave.
   * Mesma lógica do MainDashboard.checkPerm().
   */
  const checkPerm = (permKey) => {
    if (!permKey) return true
    if (authStore.isAdminInterno) return true
    return authStore.temModulo(permKey)
  }

  /**
   * Verifica se um grupo possui ao menos um item visível.
   */
  const hasVisibleItems = (group) => {
    if (group.adminOnly && !authStore.isAdminInterno) return false
    return group.items.some(item => !item.isHeader && checkPerm(item.perm))
  }

  /**
   * Retorna apenas os itens visíveis de um grupo, respeitando headers.
   */
  const getVisibleItems = (group) => {
    if (group.adminOnly && !authStore.isAdminInterno) return []
    const items = group.items
    const result = []
    for (let i = 0; i < items.length; i++) {
      const item = items[i]
      if (item.isHeader) {
        const nextHeaderIdx = items.findIndex((it, idx) => idx > i && it.isHeader)
        const slice = nextHeaderIdx === -1 ? items.slice(i + 1) : items.slice(i + 1, nextHeaderIdx)
        const hasVisible = slice.some(it => !it.isHeader && checkPerm(it.perm))
        if (hasVisible) result.push(item)
      } else if (checkPerm(item.perm)) {
        result.push(item)
      }
    }
    return result
  }

  /**
   * Menu completo filtrado por RBAC.
   */
  const menuComFiltroRBAC = computed(() => {
    return menuConfig
      .filter(group => hasVisibleItems(group))
      .map(group => ({
        ...group,
        items: getVisibleItems(group)
      }))
  })

  /**
   * Índice de busca plano (sem grupos) para o input de busca inline.
   */
  const menuSearchIndex = computed(() => {
    const index = []
    for (const group of menuComFiltroRBAC.value) {
      for (const item of group.items) {
        if (!item.isHeader) {
          index.push({
            module: item.module,
            title: item.title,
            group: group.label,
            icon: item.icon,
            perm: item.perm,
            idRegraAcesso: item.idRegraAcesso,
            idDocumentacao: item.idDocumentacao,
            extraProps: item.extraProps
          })
        }
      }
    }
    return index
  })

  return {
    checkPerm,
    hasVisibleItems,
    getVisibleItems,
    menuComFiltroRBAC,
    menuSearchIndex
  }
}
